<template>
  <div
    v-loading="isVerifying"
    class="cloud-intercom"
  >
    <div class="cloud-intercom__header">
      <img
        src="@/assets/img/login/logo.webp"
        alt="智生活"
      >
    </div>
    <div class="cloud-intercom__body">
      <router-view
        v-bind="currentProps"
        @start-verify="onStartVerify"
        @retry-location="onRetryLocation"
        @go-verification="onGoVerification"
        @go-registration="onGoRegistration"
        @arrived-by-code="onArrivedByCode"
        @arrived-by-form="onArrivedByForm"
        @community-name="communityName = $event"
        @guest-token-expired="onGuestTokenExpired"
        @permission-granted="onPermissionGranted"
        @permission-denied="onPermissionDenied"
        @retry-media="onRetryMedia"
      />
    </div>
  </div>
</template>

<script>
import { apiVoip } from '@/api/Voip'
import dayjs from 'dayjs'

/**
 * 將 server 回傳的 expireAtUtc 字串統一解析為 dayjs 物件（UTC 語意）。
 * server 可能回傳不含時區後綴（e.g. "2024-01-01T10:00:00"）或含 Z（"...Z"）的 UTC 時間；
 * 不論哪種格式，都補上 Z 強制 UTC 解析，避免 dayjs 依賴本地時區造成偏差。
 * @param {string} dateStr
 * @returns {dayjs.Dayjs}
 */
function parseUtcExpiry (dateStr) {
  if (!dateStr) return dayjs(0)
  const normalized = /Z$|[+-]\d{2}:\d{2}$/.test(dateStr) ? dateStr : dateStr + 'Z'
  return dayjs(normalized)
}

/** guestToken session 有效期（30 分鐘）。超過此時間視為過期，使用者需重新完成驗證流程。 */
const GUEST_SESSION_TTL_MS = 30 * 60 * 1000

/** 路由名稱常數，對應各子路由 */
const ROUTE = {
  WELCOME: 'cloudIntercomWelcome',
  BLOCKING: 'cloudIntercomBlocking',
  LOCATION_DENIED: 'cloudIntercomLocationDenied',
  OUT_OF_RANGE: 'cloudIntercomOutOfRange',
  MEDIA_PERMISSION: 'cloudIntercomMediaPermission',
  MEDIA_DENIED: 'cloudIntercomMediaDenied',
  HOME: 'cloudIntercomHome',
  VERIFICATION: 'cloudIntercomVerification',
  REGISTRATION: 'cloudIntercomRegistration',
  CALLEES: 'cloudIntercomCallees',
  TIMEOUT: 'cloudIntercomTimeout'
}

export default {
  name: 'CloudIntercom',
  data () {
    return {
      // 由 RegistrationPage 呼叫 GetOnSiteRegisterFormInfo 後透過 emit 傳入
      communityName: '',
      // ValidateGeolocation 成功後取得，用於後續訪客 API 驗權
      geolocationToken: '',
      // 10 分鐘有效期截止時間（server 回傳 UTC+0 原始值，比較時各自加 8 小時）
      expireAtUtc: null,
      // ArrivedInfo / CreateAndArrived 成功後取得，傳入撥打確認頁
      guestToken: '',
      // 地理驗證 session（GeolocationToken）倒數計時器 handle
      expiryTimer: null,
      // 訪客 session（guestToken）倒數計時器 handle
      guestExpiryTimer: null,
      // 「開始驗證」流程進行中（reCAPTCHA → 定位 → API）
      isVerifying: false
    }
  },
  computed: {
    /** 從路由參數取得 QR Code 內的哨點識別碼 */
    publicCode () {
      return this.$route.params.publicCode
    },
    /** 依當前子路由決定要傳入子元件的 props */
    currentProps () {
      const base = { communityName: this.communityName }
      const route = this.$route.name
      if (route === ROUTE.VERIFICATION || route === ROUTE.REGISTRATION) {
        return { ...base, geolocationToken: this.geolocationToken }
      }
      if (route === ROUTE.CALLEES) {
        return { ...base, guestToken: this.guestToken }
      }
      return base
    }
  },
  watch: {
    '$route' (to) {
      this.validateRouteEntry(to.name)
    }
  },
  created () {
    // 子元件（CalleesPage、VerificationPage 等）在 CloudIntercom mounted 之前就 mount，
    // 若等到 mounted → checkContractStatus 才設 guestToken，子元件拿到的 prop 會是空字串。
    // 在 created 同步預填 sessionStorage 的值，確保子元件 mount 時 props 已有正確資料。
    this.prefillSessionState()
  },
  async mounted () {
    await this.checkContractStatus()
  },
  beforeDestroy () {
    this.clearExpiryTimer()
    this.clearGuestExpiryTimer()
  },
  methods: {
    /**
     * 在 created hook 同步預填 sessionStorage 中的 guestToken / geolocationToken，
     * 確保子元件 mount 時透過 currentProps 拿到正確 props，不必等到 mounted → checkContractStatus。
     * 完整的有效性驗證與導頁邏輯仍由 checkContractStatus 負責。
     */
    prefillSessionState () {
      try {
        const guestRaw = sessionStorage.getItem(`ci_guest_${this.publicCode}`)
        if (guestRaw) {
          const { guestToken, communityName, savedAt } = JSON.parse(guestRaw)
          if (guestToken && savedAt && Date.now() - savedAt <= GUEST_SESSION_TTL_MS) {
            this.guestToken = guestToken
            this.communityName = communityName || this.communityName
          }
        }
      } catch { /* ignore */ }

      try {
        const geoRaw = sessionStorage.getItem(`ci_geo_${this.publicCode}`)
        if (geoRaw) {
          const { geolocationToken, expireAtUtc } = JSON.parse(geoRaw)
          if (geolocationToken && expireAtUtc && !dayjs().isAfter(parseUtcExpiry(expireAtUtc))) {
            this.geolocationToken = geolocationToken
            this.expireAtUtc = expireAtUtc
          }
        }
      } catch { /* ignore */ }
    },

    /**
     * 導頁至指定子路由，若已在目標路由則略過（避免 NavigationDuplicated 錯誤）。
     * @param {string} routeName - ROUTE 常數之一
     * @param {boolean} [replace=false] - true 為取代當前 history（程式自動重導用），false 為新增 history entry（使用者操作用）
     */
    navigate (routeName, replace = false) {
      if (this.$route.name !== routeName) {
        const target = { name: routeName, params: { publicCode: this.publicCode } }
        replace ? this.$router.replace(target) : this.$router.push(target)
      }
    },

    /**
     * 返回鍵觸發路由切換時，驗證目標頁面的前提條件是否滿足。
     * 若 geo session 或 guest session 已失效，導回歡迎頁。
     * @param {string} routeName
     */
    validateRouteEntry (routeName) {
      const geoRequiredRoutes = [
        ROUTE.HOME, ROUTE.VERIFICATION, ROUTE.REGISTRATION,
        ROUTE.MEDIA_PERMISSION, ROUTE.MEDIA_DENIED
      ]
      if (geoRequiredRoutes.includes(routeName)) {
        const geoExpired = !this.geolocationToken || !this.expireAtUtc ||
          dayjs().isAfter(parseUtcExpiry(this.expireAtUtc))
        if (geoExpired && !this.restoreGeoSession()) {
          this.navigate(ROUTE.WELCOME, true)
        }
      }
      if (routeName === ROUTE.CALLEES && !this.guestToken) {
        if (!this.restoreGuestSession()) {
          this.navigate(ROUTE.WELCOME, true)
        }
      }
    },

    /**
     * 進頁後立即查詢此 QR Code 對應的社區是否擁有雲對講 Air 合約
     * API 失敗時保守地顯示阻擋頁，避免無合約社區誤通
     */
    async checkContractStatus () {
      try {
        const res = await apiVoip.Community.GetStatus({ PublicCode: this.publicCode })
        if (res.data.Community) this.communityName = res.data.Community
        if (res.data.IsActive) {
          const currentRoute = this.$route.name
          if (currentRoute === ROUTE.CALLEES) {
            if (!this.restoreGuestSession()) {
              this.navigate(ROUTE.WELCOME, true)
            }
            return
          }
          const geoRequiredRoutes = [
            ROUTE.HOME, ROUTE.VERIFICATION, ROUTE.REGISTRATION,
            ROUTE.MEDIA_PERMISSION, ROUTE.MEDIA_DENIED
          ]
          if (geoRequiredRoutes.includes(currentRoute)) {
            if (!this.restoreGeoSession()) {
              this.navigate(ROUTE.WELCOME, true)
            }
          }
        } else {
          this.navigate(ROUTE.BLOCKING, true)
        }
      } catch {
        this.navigate(ROUTE.BLOCKING, true)
      }
    },

    /**
     * 歡迎頁「開始驗證」按鈕觸發
     * reCAPTCHA 須在 geolocation 請求前取得，token 不可重用
     */
    async onStartVerify () {
      this.isVerifying = true
      try {
        await this.$recaptchaLoaded()
        const recaptchaToken = await this.$recaptcha('software_entry_point_geolocation_validation')
        this.requestGeolocation(recaptchaToken)
      } catch {
        this.isVerifying = false
      }
    },

    /**
     * 呼叫瀏覽器 Geolocation API 取得使用者座標
     * recaptchaToken 須透過參數傳入，因為 getCurrentPosition 是非同步 callback，不能在內部再次執行 reCAPTCHA
     * @param {string} recaptchaToken
     */
    requestGeolocation (recaptchaToken) {
      if (!navigator.geolocation) {
        this.navigate(ROUTE.LOCATION_DENIED)
        this.isVerifying = false
        return
      }
      navigator.geolocation.getCurrentPosition(
        (position) => this.validateGeolocation(position, recaptchaToken),
        () => {
          this.navigate(ROUTE.LOCATION_DENIED)
          this.isVerifying = false
        }
      )
    },

    /**
     * 將座標送至後端驗證地理圍欄，成功後儲存 token 並啟動倒數計時器
     * server 回傳 UTC 時間（含或不含 Z 後綴），統一由 parseUtcExpiry 正確解析後比較
     * 400005（超出範圍）或其他錯誤皆導向 OUT_OF_RANGE，由使用者決定是否重試
     * @param {GeolocationPosition} position
     * @param {string} recaptchaToken
     */
    async validateGeolocation (position, recaptchaToken) {
      try {
        const res = await apiVoip.Community.ValidateGeolocation({
          PublicCode: this.publicCode,
          Latitude: position.coords.latitude,
          Longitude: position.coords.longitude,
          RecaptchaToken: recaptchaToken
        })
        this.expireAtUtc = res.data.ExpireAtUtc
        this.geolocationToken = res.data.GeolocationToken
        this.saveGeoSession()
        this.startExpiryTimer()
        this.$message({ type: 'success', message: this.$t('Intercom.CloudIntercom.Common.VerificationSucceeded') })
        const alreadyGranted = await this.checkMediaPermissions()
        this.navigate(alreadyGranted ? ROUTE.HOME : ROUTE.MEDIA_PERMISSION)
      } catch {
        this.navigate(ROUTE.OUT_OF_RANGE)
      } finally {
        this.isVerifying = false
      }
    },

    /**
     * 根據 expireAtUtc 計算剩餘毫秒並啟動 setTimeout
     * 到期時若不在撥打確認頁，自動跳轉至 TimeoutPage
     */
    startExpiryTimer () {
      this.clearExpiryTimer()
      const remaining = parseUtcExpiry(this.expireAtUtc) - dayjs()
      if (remaining <= 0) {
        this.clearGuestSession()
        this.clearGeoSession()
        this.navigate(ROUTE.TIMEOUT, true)
        return
      }
      this.expiryTimer = setTimeout(() => {
        this.clearGuestSession()
        this.clearGeoSession()
        // 已進入撥打確認頁時由該頁面自行處理逾時，不在此重複跳轉
        if (this.$route.name !== ROUTE.CALLEES) {
          this.navigate(ROUTE.TIMEOUT, true)
        }
      }, remaining)
    },

    /** 清除倒數計時器，避免重新驗證或元件銷毀時重複觸發 */
    clearExpiryTimer () {
      if (this.expiryTimer) {
        clearTimeout(this.expiryTimer)
        this.expiryTimer = null
      }
    },

    /** 使用者在超出範圍頁或定位被拒頁選擇重新驗證時，直接原地重跑驗證流程 */
    onRetryLocation () {
      this.onStartVerify()
    },

    /** 使用者選擇「已登記訪客（有驗證碼）」 */
    onGoVerification () {
      this.navigate(ROUTE.VERIFICATION)
    },

    /** 使用者選擇「現場登記訪客（無驗證碼）」 */
    onGoRegistration () {
      this.navigate(ROUTE.REGISTRATION)
    },

    /**
     * 已登記訪客驗證碼驗證成功後的回調
     * @param {{ guestToken: string }} payload
     */
    onArrivedByCode ({ guestToken }) {
      this.guestToken = guestToken
      this.goToCallees()
    },

    /**
     * 現場訪客登記成功後的回調
     * @param {{ guestToken: string }} payload
     */
    onArrivedByForm ({ guestToken }) {
      this.guestToken = guestToken
      this.goToCallees()
    },

    /**
     * 進入撥打確認頁前再次確認是否逾時，
     * 防止使用者在表單停留超時後才送出
     */
    goToCallees () {
      if (this.expireAtUtc && dayjs().isAfter(parseUtcExpiry(this.expireAtUtc))) {
        this.navigate(ROUTE.TIMEOUT, true)
        return
      }
      this.clearGeoSession()
      this.saveGuestSession()
      this.navigate(ROUTE.CALLEES)
    },

    /**
     * 將 guestToken、communityName 與儲存時間戳寫入 sessionStorage，以 publicCode 為範圍，
     * 確保不同 QR Code 的 session 不互相干擾。
     * 在進入 CalleesPage 前呼叫，使重新整理後仍能恢復至此頁。
     * savedAt 用於 restoreGuestSession / startGuestExpiryTimer 計算剩餘效期。
     */
    saveGuestSession () {
      sessionStorage.setItem(
        `ci_guest_${this.publicCode}`,
        JSON.stringify({
          guestToken: this.guestToken,
          communityName: this.communityName,
          savedAt: Date.now()
        })
      )
      this.startGuestExpiryTimer(GUEST_SESSION_TTL_MS)
    },

    /**
     * 清除 sessionStorage 中的訪客 session 並停止計時器。
     * 在 guestToken 失效或倒數到期時呼叫，確保使用者下次進入時從頭驗證。
     */
    clearGuestSession () {
      sessionStorage.removeItem(`ci_guest_${this.publicCode}`)
      this.clearGuestExpiryTimer()
    },

    /**
     * 嘗試從 sessionStorage 恢復 guestToken 狀態（不導頁）。
     * 恢復時依 savedAt 計算實際剩餘效期並重新啟動倒數計時器。
     * guestToken 是否仍有效，待 CalleesPage 發起 API 後由回應決定。
     * @returns {boolean} 是否成功恢復 session
     */
    restoreGuestSession () {
      try {
        const raw = sessionStorage.getItem(`ci_guest_${this.publicCode}`)
        if (!raw) return false
        const { guestToken, communityName, savedAt } = JSON.parse(raw)
        if (!guestToken) return false
        const elapsed = savedAt ? Date.now() - savedAt : GUEST_SESSION_TTL_MS
        if (elapsed >= GUEST_SESSION_TTL_MS) {
          this.clearGuestSession()
          return false
        }
        this.guestToken = guestToken
        this.communityName = communityName || this.communityName
        this.startGuestExpiryTimer(GUEST_SESSION_TTL_MS - elapsed)
        return true
      } catch {
        return false
      }
    },

    /**
     * 依剩餘毫秒啟動 guestToken 前端倒數計時器。
     * 到期時清除 session 並強制導回歡迎頁，即使後端尚未回傳錯誤也能保護使用者。
     * @param {number} remaining 剩餘毫秒數
     */
    startGuestExpiryTimer (remaining) {
      this.clearGuestExpiryTimer()
      if (remaining <= 0) {
        this.clearGuestSession()
        this.navigate(ROUTE.WELCOME, true)
        return
      }
      this.guestExpiryTimer = setTimeout(() => {
        this.clearGuestSession()
        this.navigate(ROUTE.WELCOME, true)
      }, remaining)
    },

    /** 清除 guestToken 倒數計時器，避免元件銷毀或 session 清除時重複觸發 */
    clearGuestExpiryTimer () {
      if (this.guestExpiryTimer) {
        clearTimeout(this.guestExpiryTimer)
        this.guestExpiryTimer = null
      }
    },

    /**
     * 由 CalleesPage 在偵測到 guestToken 失效時 emit `guest-token-expired` 觸發。
     * 清除 session 並導回歡迎頁，讓使用者重新走完驗證流程。
     */
    onGuestTokenExpired () {
      this.clearGuestSession()
      this.navigate(ROUTE.WELCOME, true)
    },

    /** MediaPermissionPage 授權成功後進入首頁 */
    onPermissionGranted () {
      this.navigate(ROUTE.HOME)
    },

    /**
     * 查詢麥克風與攝影機是否已授權，用於決定是否跳過 MediaPermissionPage。
     * Safari 等不支援 permissions.query 的瀏覽器會 catch 並回傳 false（保守顯示權限頁）。
     * @returns {Promise<boolean>}
     */
    async checkMediaPermissions () {
      try {
        const [cam, mic] = await Promise.all([
          navigator.permissions.query({ name: 'camera' }),
          navigator.permissions.query({ name: 'microphone' })
        ])
        return cam.state === 'granted' && mic.state === 'granted'
      } catch {
        return false
      }
    },

    /**
     * 將 geolocationToken 與 expireAtUtc 寫入 sessionStorage，以 publicCode 為範圍。
     * 在地理驗證成功後呼叫，使重新整理後仍能恢復至 HOME 或 MEDIA_PERMISSION。
     */
    saveGeoSession () {
      sessionStorage.setItem(
        `ci_geo_${this.publicCode}`,
        JSON.stringify({
          geolocationToken: this.geolocationToken,
          expireAtUtc: this.expireAtUtc
        })
      )
    },

    /**
     * 清除 sessionStorage 中的地理驗證 session。
     * 在 GeolocationToken 到期或使用者進入 CalleesPage 時呼叫。
     */
    clearGeoSession () {
      sessionStorage.removeItem(`ci_geo_${this.publicCode}`)
    },

    /**
     * 嘗試從 sessionStorage 恢復地理驗證 session。
     * 若找到且未過期，還原 geolocationToken / expireAtUtc 並啟動倒數計時器。
     * @returns {boolean} 是否成功恢復
     */
    restoreGeoSession () {
      try {
        const raw = sessionStorage.getItem(`ci_geo_${this.publicCode}`)
        if (!raw) return false
        const { geolocationToken, expireAtUtc } = JSON.parse(raw)
        if (!geolocationToken || !expireAtUtc) return false
        if (dayjs().isAfter(parseUtcExpiry(expireAtUtc))) {
          this.clearGeoSession()
          return false
        }
        this.geolocationToken = geolocationToken
        this.expireAtUtc = expireAtUtc
        this.startExpiryTimer()
        return true
      } catch {
        return false
      }
    },

    /** MediaPermissionPage 授權被拒後導至 MediaDeniedPage */
    onPermissionDenied () {
      this.navigate(ROUTE.MEDIA_DENIED)
    },

    /**
     * MediaDeniedPage 點擊重新整理時觸發：重新查詢媒體權限狀態。
     * 若使用者已在系統設定中手動開啟，則直接進入首頁。
     */
    async onRetryMedia () {
      this.isVerifying = true
      await new Promise(resolve => setTimeout(resolve, 600))
      const granted = await this.checkMediaPermissions()
      this.isVerifying = false
      if (granted) this.navigate(ROUTE.HOME, true)
    }
  }
}
</script>

<style lang="scss">
.ci-alert {
  width: calc(100vw - 48px);
  max-width: 360px;
}
</style>

<style lang="scss" scoped>
.cloud-intercom {
  display: flex;
  flex-direction: column;
  min-height: 100vh; // fallback for browsers without dvh support
  min-height: 100dvh;
  background-color: #fff;

  &__header {
    position: relative;
    height: 54px;
    background-color: #f9f9f9;
    box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
    display: flex;
    align-items: center;
    padding: 0 24px;
    flex-shrink: 0;

    img {
      height: 32px;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    height: calc(100% - 54px);
  }
}
</style>
